// package imports
import React, { useState } from 'react';

// CSS imports
import './EmailSignup.css';

// Component imports
import TextInput from '../../inputs/textInput/TextInput';
import PrimaryButton from '../../buttons/primaryButton/PrimaryButton';
import Alert from '../../alert/Alert';

// Service imports
import { ApiService } from '../../../services/ApiService';

// Icons imports
import { MdError } from 'react-icons/md';
import { IoChevronBack } from 'react-icons/io5';

// Type imports
import { LoginPage } from '../../../types/login/LoginPage';
import { SignupLoginInput } from '../../../types/authentication/SignupLoginInput';
import { SignupResultType } from '../../../types/authentication/SignupResultType';
import { AlertSize } from '../../../types/alert/AlertSize';

type Props = {
  switchPage: (x: LoginPage) => void;
  setIsLoading: (x: boolean) => void;
};

const EmailSignup: React.FC<Props> = ({ switchPage, setIsLoading }: Props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [showUnknownError, setShowUnknownError] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'email': {
        setEmail(e.target.value);
        setEmailError('');
        break;
      }
      case 'password': {
        setPassword(e.target.value);
        setPasswordError('');
        break;
      }
    }
  };

  const triggerSignup = () => {
    const isValid = checkInputForError();
    if (isValid) {
      signup();
    }
  };

  const signup = async () => {
    setIsLoading(true);
    const providedUser: SignupLoginInput = {
      email: email,
      password: password,
    };

    const res: SignupResultType = await ApiService.signup(providedUser);

    setIsLoading(false);

    if (res === SignupResultType.OK) {
      switchPage(LoginPage.SIGNUPHINT);
    } else if (res === SignupResultType.DUPLICATE) {
      setShowDuplicateError(true);
    } else {
      setShowUnknownError(true);
    }
  };

  const checkInputForError = () => {
    let isValid = true;

    if (email === '') {
      isValid = false;
      setEmailError('Please enter an email');
    } else if (!/[^@\s]+@[^@\s]+\.[^@\s]+/.test(email)) {
      isValid = false;
      setEmailError('Please enter a valid email');
    }

    if (password === '') {
      isValid = false;
      setPasswordError('Please enter a password');
    } else if (password.length < 8) {
      isValid = false;
      setPasswordError('Your password must include at least 8 characters');
    }

    return isValid;
  };
  return (
    <div className='EmailSignup'>
      <div className='modal-header'>
        <h1>Sign up</h1>
        <div
          className='back-container'
          onClick={() => switchPage(LoginPage.SIGNUP)}
        >
          <IoChevronBack className='back-chevron' />
          <span>Back</span>
        </div>
      </div>
      <TextInput
        value={email}
        name='email'
        placeholder='Email'
        onChange={onChange}
        errorMessage={emailError}
        className='full-width'
      />
      <TextInput
        value={password}
        name='password'
        type='password'
        placeholder='Password'
        onChange={onChange}
        errorMessage={passwordError}
        className='full-width password-input'
      />
      <PrimaryButton
        label='Sign up with email'
        className='full-width signup-button'
        onClick={triggerSignup}
      />
      <div className='border-container'></div>
      <div className='divider'></div>
      <span>
        Already have an account?{' '}
        <span
          className='login-link'
          onClick={() => switchPage(LoginPage.LOGIN)}
        >
          Log in
        </span>
      </span>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
      <Alert
        showAlert={showDuplicateError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowDuplicateError}
      >
        <MdError size={20} />
        <span>An account with this email already exists!</span>
      </Alert>
    </div>
  );
};

export default EmailSignup;
